import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AppState, Message, Forum, Contact, ZohoAuth, AppError } from '@/types';

interface AppStore extends AppState {
  // Actions pour l'authentification
  setAuth: (auth: ZohoAuth | null) => void;
  clearAuth: () => void;
  
  // Actions pour le portail
  setSelectedPortal: (portal: { id: string; name: string; portal_name: string } | null) => void;
  
  // Actions pour les forums
  setForums: (forums: Forum[]) => void;
  addForum: (forum: Forum) => void;
  updateForum: (id: string, updates: Partial<Forum>) => void;
  removeForum: (id: string) => void;
  
  // Actions pour les contacts
  setContacts: (contacts: Contact[]) => void;
  addContact: (contact: Contact) => void;
  updateContact: (id: string, updates: Partial<Contact>) => void;
  removeContact: (id: string) => void;
  
  // Actions pour les messages
  setCurrentMessage: (message: Message | null) => void;
  updateCurrentMessage: (updates: Partial<Message>) => void;
  clearCurrentMessage: () => void;
  
  // Actions pour l'état de chargement
  setLoading: (loading: boolean) => void;
  
  // Actions pour les erreurs
  setError: (error: AppError | null) => void;
  clearError: () => void;
}

export const useAppStore = create<AppStore>()(
  persist(
    (set) => ({
      // État initial
      auth: null,
      selectedPortal: null,
      forums: [],
      contacts: [],
      currentMessage: null,
      isLoading: false,
      error: null,

      // Actions pour l'authentification
      setAuth: (auth) => set({ auth }),
      clearAuth: () => set({ auth: null, selectedPortal: null }),
      
      // Actions pour le portail
      setSelectedPortal: (portal) => set({ selectedPortal: portal }),

      // Actions pour les forums
      setForums: (forums) => set({ forums }),
      addForum: (forum) => set((state) => ({ 
        forums: [...state.forums, forum] 
      })),
      updateForum: (id, updates) => set((state) => ({
        forums: state.forums.map(forum => 
          forum.id === id ? { ...forum, ...updates } : forum
        )
      })),
      removeForum: (id) => set((state) => ({
        forums: state.forums.filter(forum => forum.id !== id)
      })),

      // Actions pour les contacts
      setContacts: (contacts) => set({ contacts }),
      addContact: (contact) => set((state) => ({ 
        contacts: [...state.contacts, contact] 
      })),
      updateContact: (id, updates) => set((state) => ({
        contacts: state.contacts.map(contact => 
          contact.id === id ? { ...contact, ...updates } : contact
        )
      })),
      removeContact: (id) => set((state) => ({
        contacts: state.contacts.filter(contact => contact.id !== id)
      })),

      // Actions pour les messages
      setCurrentMessage: (message) => set({ currentMessage: message }),
      updateCurrentMessage: (updates) => set((state) => ({
        currentMessage: state.currentMessage 
          ? { ...state.currentMessage, ...updates }
          : null
      })),
      clearCurrentMessage: () => set({ currentMessage: null }),

      // Actions pour l'état de chargement
      setLoading: (isLoading) => set({ isLoading }),

      // Actions pour les erreurs
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),
    }),
    {
      name: 'zoho-mailer-store',
      partialize: (state) => ({
        auth: state.auth,
        forums: state.forums,
        contacts: state.contacts,
        currentMessage: state.currentMessage,
      }),
    }
  )
); 